import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardContent } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { WeightConfig } from '@/sections/customerManagement/types';
import {
  METRIC_CALCULATION_INFO,
  METRIC_DISPLAY_ORDER,
} from '@/sections/customerManagement/utils/leaderboardTransforms';

interface Props {
  weights: WeightConfig;
}

const METRIC_LABELS: Record<string, string> = {
  manual_discoveries: 'Manual Discoveries',
  automation_authored: 'Automation Authored',
  accurate_triage: 'Accurate Triage',
  assists: 'Assists',
  manual_to_automated: 'Manual → Automated',
  code_commits: 'Code Commits',
  cve_bounties: 'CVEs/Bounties',
  auto_triage_rules: 'Auto-Triage Rules',
  effective_triage: 'Effective Triage',
};

function formatWeight(value: number): string {
  return `${value}`;
}

const SEVERITY_ORDER = ['C', 'H', 'M', 'L', 'I'];

const SEVERITY_LABELS: Record<string, string> = {
  C: 'Critical',
  H: 'High',
  M: 'Medium',
  L: 'Low',
  I: 'Info',
};

export function WeightsSummary({ weights }: Props) {
  return (
    <Card>
      <CardContent className="p-0">
        <Accordion type="single" collapsible>
          <AccordionItem value="weights" className="border-none">
            <AccordionTrigger className="px-4 py-3 text-sm">
              Scoring Weights
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-chariot-text-secondary mb-2 text-xs font-medium tracking-wide uppercase">
                    Metric Weights
                  </h4>
                  <div className="space-y-1">
                    {METRIC_DISPLAY_ORDER.filter(
                      key => key in weights.metric_weights
                    ).map(key => {
                      const info = METRIC_CALCULATION_INFO[key];
                      const row = (
                        <div className="text-chariot-text-primary flex justify-between text-sm">
                          <span>{METRIC_LABELS[key] ?? key}</span>
                          <span className="text-chariot-text-secondary">
                            {formatWeight(weights.metric_weights[key])}
                          </span>
                        </div>
                      );
                      if (!info) return <div key={key}>{row}</div>;
                      return (
                        <TooltipProvider key={key}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="cursor-help">{row}</div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              <div className="space-y-1 text-xs">
                                <p className="font-medium">
                                  {METRIC_LABELS[key]}
                                </p>
                                <p>{info.formula}</p>
                                <p className="text-chariot-text-secondary italic">
                                  e.g. {info.example}
                                </p>
                                <p className="text-chariot-text-secondary">
                                  Score = raw x {weights.metric_weights[key]}
                                </p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <h4 className="text-chariot-text-secondary mb-2 text-xs font-medium tracking-wide uppercase">
                    Severity Weights
                  </h4>
                  <div className="space-y-1">
                    {SEVERITY_ORDER.filter(
                      key => key in weights.severity_weights
                    ).map(key => (
                      <div
                        key={key}
                        className="text-chariot-text-primary flex justify-between text-sm"
                      >
                        <span>{SEVERITY_LABELS[key] ?? key}</span>
                        <span className="text-chariot-text-secondary">
                          {formatWeight(weights.severity_weights[key])}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {weights.updated_by && (
                <p className="text-chariot-text-secondary mt-4 text-xs">
                  Last updated by {weights.updated_by}
                  {weights.updated_at && ` on ${weights.updated_at}`}
                </p>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}
